import { apiClient } from "module/api";

/**
 * In-memory cache of raw link -> cosmetic short link, so repeated QR re-renders
 * (e.g. changing colors in the style panel) don't re-hit the API for the same URL.
 */
const cosmeticShortLinkCache = new Map<string, string>();

/**
 * Returns a cosmetic short link to render the QR *image* on.
 *
 * IMPORTANT: this is purely cosmetic. The raw URL must still be stored in the
 * shape's `qrCodeLink` prop / `data-qr-link` so the print pipeline (puppeteer)
 * keeps generating its own tracked short link (`/external-redirect` + `/short/{hash}`).
 *
 * Falls back to the raw link if shortening fails (offline/local/API down) so the
 * QR never breaks.
 */
export async function getCosmeticQrShortLink(rawLink: string): Promise<string> {
  if (!rawLink) return rawLink;
  // Never re-shorten an already-short link (avoids a short of a short).
  if (rawLink.includes("/short/")) return rawLink;

  const cached = cosmeticShortLinkCache.get(rawLink);
  if (cached) return cached;

  const shortLink = await apiClient.createShortUrl(rawLink);
  // Only cache successful shortenings — never cache the raw fallback, otherwise a
  // transient/early API failure would permanently pin the QR + input to the long URL.
  if (shortLink) {
    cosmeticShortLinkCache.set(rawLink, shortLink);
    return shortLink;
  }
  return rawLink;
}
